import React, { useMemo } from 'react';
import { Box, BoxProps, Grid2 as Grid, Typography } from '@mui/material';
import { useShowContext } from 'framework/show';
import { ScrollToSectionAnchor } from 'framework/ui/Anchor';
import type { Data } from 'framework/types';
import { styled, SxProps } from '@mui/material/styles';
import clsx from 'clsx';
import { viewSingleEntrySx } from 'framework/ui/styles';

const StyledBox = styled(Box)<BoxProps>(({ theme }: { theme: any }) => ({
  marginBottom: '20px',
  '& h4': {
    display: 'flex',
    alignItems: 'flex-start',
  },
  '& p': {
    margin: 0,
  },

  '.no-padding': {
    padding: '0 !important',
  },
}));

interface ShowSectionProps {
  title?: string | React.ReactElement | ((data: Data) => string | React.ReactElement);
  titleId?: string;
  titleStartAdornment?: React.ReactNode;
  icon?: React.ReactNode;
  children: React.ReactElement | React.ReactElement[] | React.ReactNode;
  isVisible?: boolean | ((data: Data) => boolean);
  hasAnchorLink?: boolean;
  titleVariant?: 'h2' | 'h3' | 'h4' | 'h5';
  actions?: React.ReactNode;
  justifyContent?: string;
  sx?: SxProps;
  disableDefaultPadding?: boolean;
}

const ShowSection = ({
  title,
  titleId,
  titleStartAdornment,
  icon,
  children,
  isVisible = true,
  hasAnchorLink = false,
  titleVariant = 'h4',
  actions,
  justifyContent = 'flex-start',
  sx,
  disableDefaultPadding,
}: ShowSectionProps) => {
  const { data } = useShowContext();

  const isSectionsVisible = useMemo(
    () => (typeof isVisible === 'function' ? isVisible(data) : isVisible),
    [isVisible, data],
  );

  const titleHeader = typeof title === 'function' ? title(data) : title;

  if (!isSectionsVisible) return null;

  return (
    <StyledBox
      sx={sx}
      component="section"
      className={clsx('section-details__wrapper', {
        'no-padding': disableDefaultPadding,
      })}
      data-hj-suppress
    >
      {titleHeader ? (
        <>
          <Grid container className="show-title-container" sx={{ marginBottom: { xs: '28px', sm: '30px' } }}>
            <Grid
              size={12}
              sx={
                actions
                  ? {
                      display: { xs: 'block', sm: 'flex' },
                      justifyContent,
                    }
                  : {}
              }
            >
              <Box display="flex" alignItems="center" gap={1}>
                {titleStartAdornment}
                <Typography
                  variant={titleVariant}
                  color={titleVariant === 'h2' ? 'brand' : 'primary'}
                  sx={{ flexGrow: 1 }}
                >
                  {titleHeader}
                  <ScrollToSectionAnchor
                    titleId={titleId || (typeof titleHeader === 'string' ? titleHeader : '')}
                    isLinkDiplayed={hasAnchorLink}
                  />
                </Typography>
              </Box>
              {actions ? (
                <Box
                  className="section-details__actions"
                  sx={{
                    display: 'flex',
                    justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                    alignItems: 'center',
                    mt: { xs: '20px', sm: '-4px' },
                    mb: { xs: 0, sm: '-4px' },
                    ml: { xs: 0, sm: 3 },
                    'button,a:not(:last-child)': {
                      mr: '10px',
                    },
                  }}
                >
                  {actions}
                </Box>
              ) : null}
            </Grid>
          </Grid>
          <Grid container>
            {icon && <Grid>{icon}</Grid>}
            <Grid size="grow">
              <Box sx={viewSingleEntrySx}>{children}</Box>
            </Grid>
          </Grid>
        </>
      ) : (
        <Grid container alignItems="center">
          {icon && (
            <Box
              sx={{
                paddingLeft: '8px',
                display: 'flex',
                justifyContent: 'center',
                flexShrink: 0,
                placeSelf: 'flex-start',
                marginTop: '6px',
              }}
            >
              <Box
                sx={{
                  width: 25,
                  height: 25,
                  position: 'relative',
                }}
              >
                {icon}
              </Box>
            </Box>
          )}
          <Grid size="grow">
            <Box sx={viewSingleEntrySx}>{children}</Box>
          </Grid>
          {actions ? <Grid sx={{ placeSelf: 'flex-start' }}>{actions}</Grid> : null}
        </Grid>
      )}
    </StyledBox>
  );
};

export default ShowSection;
